// eslint-disable-next-line import/named
import {
  addQuotePriceSummaryToDataTracker,
  convertQuoteToQuotePriceSummaryDataContainer,
  QuoteDealerPriceAdjustmentDialog,
  QuotePriceSummaryDataContainer
} from './quote-dealer-price-adjustment-dialog';
import { awaitableTemplate } from '../../../../webmodule-common/other/ui/template-processor.js';
import type { TableColumnConfiguration } from '../../../api/dealer-api-interface-franchisee.js';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, query, state } from 'lit/decorators.js';
import { DataBinding } from '../../../../webmodule-common/other/ui/databinding/databinding.js';
import { DataTracker, FieldType } from '../../../../webmodule-common/other/ui/databinding/data-tracker.js';
import { firstValidString } from '../../../../webmodule-common/other/ui/string-helper-functions.js';
import { FormInputAssistant } from '../../../../webmodule-common/other/ui/templateresult/form-input-assistant.js';
import type { QCQuoteValues, QCValue } from '../../../../webmodule-common/interop/webmodule-interop.js';
import { getApiFactory } from '../../../api/api-injector.js';
import { isShipping } from '../../../quotes/data/quote-helper-functions.js';
import type { TemplateResult } from 'lit';
import { html, nothing } from 'lit';
import { interpolateExternal } from '../../../../localization/global';
import { LangFranchiseeQuoteItemsView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-items-view-lang';
import { QuoteItemsView, QuoteItemsViewOptions, SettingsManager } from '../../../quotes/views/quote-items-view.js';

import { QuoteState } from '../../../api/dealer-api-interface-quote.js';
import { saveWithIndicator } from '../../../../webmodule-common/other/save-workflow.js';
import { tryAsNumberString } from '../../../../webmodule-common/other/number-utilities.js';
import { userDataStore } from '../../common/current-user-data-store.js';
import type { EventTemplate } from '../../../../webmodule-common/other/ui/events.js';
import type { FranchiseeQuoteContainerManager } from '../data/franchisee-quote-manager.js';
import type { QuoteApi } from '../../../api/quote-api.js';
import type { V6FranchiseeQuoteProviderData } from '../data/franchisee-quote-provider-data.js';
import type { WebmoduleSelectEvent } from '../../../../webmodule-common/components/src/webmodule-components.js';
import { FranchiseeQuoteItemTable } from './franchisee-quote-item-table';

@customElement('wm-franchiseequoteitemsview')
export class FranchiseeQuoteItemsView extends QuoteItemsView {
  dataBinding: DataBinding;
  dataTracker: DataTracker;
  quoteApi: QuoteApi = getApiFactory().quote();
  supplierApi = getApiFactory().supplier();
  franchiseeApi = getApiFactory().franchisee();
  @query('#franchisee-quote-item-table')
  table?: FranchiseeQuoteItemTable;
  @state()
  summaryData?: QuotePriceSummaryDataContainer;
  private canUpdatePricingMetric: boolean = true;

  constructor(options: QuoteItemsViewOptions) {
    super(options);

    this.dataBinding = new DataBinding(
      this.ui,
      undefined,
      (input: string, internalId: string) => `quoteprice-${input}-${internalId}`
    );
    this.dataTracker = new DataTracker(this.dataBinding);

    const addField = (
      fieldName: string,
      propertyType?: FieldType,
      nullable?: boolean,
      editorFieldName?: string,
      data?: () => any
    ) => {
      this.dataTracker.addObjectBinding(
        data ?? (() => this.summaryData),
        fieldName,
        editorFieldName ?? fieldName,
        propertyType ?? FieldType.string,
        nullable ?? false
      );
    };

    // addField('termsAndConditions', FieldType.string, false, undefined, () => this.quoteManager.quote);

    addQuotePriceSummaryToDataTracker(() => this.summaryData, this.dataTracker);

    addField('budget', FieldType.money, true, undefined, () => this.quoteManager.quote);
  }

  protected get qcm(): FranchiseeQuoteContainerManager {
    return this.quoteManager as FranchiseeQuoteContainerManager;
  }

  refreshSummaryData() {
    this.summaryData = convertQuoteToQuotePriceSummaryDataContainer(
      this.quoteManager as FranchiseeQuoteContainerManager,
      { mergeTaxFields: true }
    );
  }

  public async refreshData(): Promise<void> {
    //this is a force reload of the data. This is not something that we want to do, if items are inuse
    //as we would end up with a bad loading of data.

    await this.table?.refreshData();
    this.requestUpdate();
  }

  quoteCalculationsEnabled() {
    return (this.quoteManager as FranchiseeQuoteContainerManager).hasQuoteCalculationData;
  }

  async afterConstruction(): Promise<void> {
    await super.afterConstruction();

    this.canUpdatePricingMetric = (this.quoteManager as FranchiseeQuoteContainerManager).canUpdatePricingMetric;

    const systemDefaults = await this.franchiseeApi.getSystemConfiguration();
    const supplierOverrides = await this.supplierApi.getSupplierQuoteConfig({
      supplierId: this.quoteManager.quote.supplierId
    });

    const v6Columns: { code: string; title: string }[] = [];

    if (this.quoteCalculationsEnabled()) {
      const data = this.quoteManager.quoteProviderData() as V6FranchiseeQuoteProviderData;
      const qcdata = data.quoteCalculations as QCQuoteValues;
      const colDefs: QCValue[] = [];
      if (qcdata) {
        qcdata.quoteItemValues.forEach(x => {
          x.values.forEach(col => {
            if (col.displayColumn && colDefs.find(x1 => x1.name == col.name) === undefined) colDefs.push(col);
          });
        });
        for (let iQCV = 0; iQCV < colDefs.length; iQCV++) {
          const qcCol = colDefs[iQCV];
          v6Columns.push({ code: qcCol.name, title: interpolateExternal(firstValidString(qcCol.caption, qcCol.name)) });
        }
      }
    }

    //Check system V6 columns
    if (
      v6Columns &&
      v6Columns.length > 0 &&
      systemDefaults?.defaultDealerConfiguration?.quoteItemColumnConfigurations
    ) {
      const v6ColConfig: TableColumnConfiguration[] = [];

      v6Columns.map(x =>
        v6ColConfig.push({
          code: x.code,
          title: x.title,
          systemDefault: false,
          defaultDisplay: true,
          canSwitch: true
        })
      );

      const insertIndex = systemDefaults.defaultDealerConfiguration.quoteItemColumnConfigurations.length - 7;

      systemDefaults.defaultDealerConfiguration.quoteItemColumnConfigurations.splice(insertIndex, 0, ...v6ColConfig);
    }

    this.settingsManager = new SettingsManager(
      systemDefaults?.defaultDealerConfiguration?.quoteItemColumnConfigurations ?? [],
      supplierOverrides?.quoteItemSummaryConfigurations ?? []
    );
  }

  public async invalidate(): Promise<void> {
    super.invalidate();
    await this.qcm.updatePricingRules();
  }

  public async prepareForSave() {
    await this.quoteManager.needsQuoteItems();
    this.dataTracker.applyChangeToValue();
  }

  getTaxLabel(): string | undefined {
    const rate = userDataStore.taxRates.find(taxRate => taxRate.rate == this.quoteManager.quotePrice.taxPercentage);

    if (rate) {
      return LangFranchiseeQuoteItemsView.taxWithRate({ taxName: rate.name, taxRate: rate.rate });
    }

    return LangFranchiseeQuoteItemsView.taxWithPercentage({
      percentage: this.quoteManager.quotePrice.taxPercentage.toFixed(2)
    });
  }

  getValidationErrors(): string[] {
    const errors = super.getValidationErrors();

    if (this.quoteManager.quoteState == QuoteState.Active || this.quoteManager.quoteState == QuoteState.Draft)
      return errors;

    if (this.quoteManager.quoteState == QuoteState.Cancelled) {
      return [];
    }

    if (
      this.quoteManager.container.items &&
      this.quoteManager.container.items.filter(x => !isShipping(x)).length == 0
    ) {
      errors.push(LangFranchiseeQuoteItemsView.noQuoteItemsInQuote());
    }

    return errors;
  }

  public eventRunPriceAdjustmentModal = async () => {
    if (
      !(await QuoteDealerPriceAdjustmentDialog.Execute(
        this.quoteManager as FranchiseeQuoteContainerManager,
        this.isClientFacing,
        this.canUpdatePricingMetric
      ))
    )
      return;

    await this.table?.refreshData();
    this.requestUpdate();

    return;
  };

  protected template(): EventTemplate {
    const forms = new FormInputAssistant(this.dataTracker, this.quoteManager.isReadonly());
    this.refreshSummaryData();

    const quoteAnnotationsTemplate = awaitableTemplate(async (): Promise<TemplateResult> => {
      if (this.qcm.hasQuoteAnnotations) {
        const data = this.qcm.quoteAnnotations;

        const validationsTemplate = (
          validationType: 'critical' | 'warning' | 'information',
          cssClass: string
        ): TemplateResult[] | TemplateResult => {
          const relatedItemsTemplate = html``; //TODO make a bullet point list of related items
          const stuff = data?.filter(x => x.severity === validationType) ?? [];
          if (stuff.length === 0) return html``;
          const rows = stuff.map(issue => {
            return html` <li class="list-group-item ${cssClass}">
              <span class="me-3 fw-bold">${interpolateExternal(validationType)}</span>${interpolateExternal(
                issue.content ?? ''
              )}
              ${relatedItemsTemplate}
            </li>`;
          });
          return html`
            <ul class="list-group">
              ${rows}
            </ul>
          `;
        };

        return html` ${validationsTemplate('critical', 'list-group-item-danger')}
        ${validationsTemplate('warning', 'list-group-item-warning')}
        ${validationsTemplate('information', 'list-group-item-info')}`;
      }
      return html``;
    });
    const quoteCalculationsTemplate = awaitableTemplate(async (): Promise<TemplateResult[]> => {
      await this.quoteManager.needsQuoteItems();

      const qcResultTemplate = (item: QCValue) => html`
        <div class="quotecalculation-col">
          <div class="quotecalculation-col-name">${firstValidString(item.caption, item.name)}</div>
          <div class="quotecalculation-col-value">
            ${firstValidString(item.displayValue, tryAsNumberString(item.value, 2))}
          </div>
        </div>
      `;

      const sections: TemplateResult[] = [];
      if (this.qcm.hasQuoteCalculationData) {
        const qcv = this.qcm.quoteCalcuationData;
        if (qcv) {
          qcv.quoteValues.forEach(element => {
            sections.push(
              html` <div class="quotecalculation-section">
                <div class="quotecalculation-label">${interpolateExternal(element.title)}</div>
                <div class="quotecalculation-result">${element.values.map(x => qcResultTemplate(x))}</div>
              </div>`
            );
          });
        }
      }
      return sections;
    });

    // const dealerName = userDataStore.franchisee.name;
    // const termsAndConditions = isEmptyOrSpace(dealerName)
    //   ? LangFranchiseeQuoteItemsView.dealerTermsAndConditions()
    //   : LangFranchiseeQuoteItemsView.dealerNameTermsAndConditions({ dealerName });

    const getMenuItems = () => {
      return this.settingsManager?.getActiveConfiguration
        .filter(x => x.canSwitch)
        .map(
          mi =>
            html` <webmodule-menu-item
              .value=${mi.code}
              type="checkbox"
              ?checked=${mi.defaultDisplay}
              disabled=${!mi.canSwitch ? true : nothing}
              >${mi.title}
            </webmodule-menu-item>`
        );
    };
    const itemsSubtotalTemplate = this.quoteManager.allowNonTaxableItems
      ? html`${forms.moneyReadonly('nonTaxableSubtotal', LangFranchiseeQuoteItemsView.zeroRatedItems())}
        ${forms.moneyReadonly('taxableSubtotal', LangFranchiseeQuoteItemsView.taxedItems())}`
      : forms.moneyReadonly('taxableSubtotal', LangFranchiseeQuoteItemsView.items());
    const priceAdjustmentsButtonTemplate = forms.clickableIcon({
      slot: 'suffix',
      library: 'fa',
      name: 'fas-sliders',
      class: 'text-primary',
      events: { click: this.eventRunPriceAdjustmentModal }
    });
    const taxTemplate = this.quoteManager.isReadonly()
      ? forms.moneyReadonly('calculatedTaxAmount', this.getTaxLabel())
      : forms.moneyReadonly('calculatedTaxAmount', this.getTaxLabel());

    const quoteItemSummaryTemplate = html`${forms.intReadonly(
      'percentMarginOrMarkup',
      this.qcm.getPricingModelLabel(),
      {
        class: 'mb-3',
        hidden: this.settingsManager?.getIsClientFacing,
        toolTip: this.qcm.getPricingModelTooltip()
      }
    )}
    ${itemsSubtotalTemplate}
    ${forms.moneyReadonly('priceAdjustment', LangFranchiseeQuoteItemsView.priceAdjustment(), {})}
    ${forms.moneyReadonly('subtotal', LangFranchiseeQuoteItemsView.itemsTotal(), { class: 'mb-3' })}${taxTemplate}
    ${forms.moneyReadonly('calculatedGrossTotal', LangFranchiseeQuoteItemsView.quoteTotal())}`;

    return html`
      <div class="page-configuration-container">
        <h2 class="page-configuration-title">${LangFranchiseeQuoteItemsView.frames()}</h2>
        <webmodule-dropdown hoist placement="bottom-end" stay-open-on-select>
          <webmodule-icon-button
            class="${classMap({
              'text-danger': !this.isClientFacing
            })}"
            slot="trigger"
            name="fas-gear"
            library="fa"
          ></webmodule-icon-button>

          <webmodule-menu @webmodule-select=${(e: WebmoduleSelectEvent) => this._onDisplayChange(e)}>
            <webmodule-menu-label>${LangFranchiseeQuoteItemsView.pageConfiguration()}</webmodule-menu-label>
            <webmodule-menu-item value="internalView"
              >${this.isClientFacing
                ? LangFranchiseeQuoteItemsView.showInternalView()
                : LangFranchiseeQuoteItemsView.showClientFacingView()}
            </webmodule-menu-item>
            <webmodule-divider></webmodule-divider>
            <webmodule-menu-label>${LangFranchiseeQuoteItemsView.tableConfiguration()}</webmodule-menu-label>
            ${getMenuItems()}
          </webmodule-menu>
        </webmodule-dropdown>
      </div>

      <franchisee-quote-item-table
        id="franchisee-quote-item-table"
        .columnConfiguration=${this.settingsManager?.getActiveConfiguration ?? []}
        .quoteManager=${this.quoteManager as FranchiseeQuoteContainerManager}
      >
      </franchisee-quote-item-table>
      ${quoteAnnotationsTemplate}
      <form class="py-3 px-0 quote-price-summary form-two-col ">
        <div class="row">
          <div class="quote-items-terms">
            <div class="row mb-2 align-items-center form-col-item"></div>
            ${quoteCalculationsTemplate}
          </div>
          <div class="quote-items-summary">
            <div class="quote-items-summary-wrapper">
              <div class="quote-items-summary-data">${quoteItemSummaryTemplate}</div>
              <div class="quote-items-summary-icons"><span>${priceAdjustmentsButtonTemplate}</span></div>
            </div>
          </div>
        </div>
      </form>
    `;
  }

  private async _onDisplayChange(e: WebmoduleSelectEvent) {
    const item = e.detail.item;

    await saveWithIndicator(async () => {
      if (item.value === 'internalView') {
        this.settingsManager?.toggleClientFacingView();
      } else {
        this.settingsManager?.toggleColumnDisplay(item.value);
      }

      return true;
    });

    this.requestUpdate();
  }
}
